import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { fetchWithAuth } from '../../services/api';

const AdminComments = () => {
  const navigate = useNavigate();
  const { lang } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const [tab, setTab] = useState(requestedTab === 'reports' ? 'reports' : 'comments');
  const [comments, setComments] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const fetchModerationData = useCallback(async () => {
    try {
      const token = localStorage.getItem('jwtToken');
      const role = localStorage.getItem('userRole');
      if (!token || role !== 'ADMIN') {
        navigate(`/${lang || 'mk'}/`);
        return;
      }

      setLoading(true);
      const [allComments, openReports] = await Promise.all([
        fetchWithAuth('/admin/comments'),
        fetchWithAuth('/admin/reports'),
      ]);
      setComments(Array.isArray(allComments) ? allComments : []);
      setReports(Array.isArray(openReports) ? openReports : []);
      setError(null);
    } catch (err) {
      console.error('Error fetching moderation data:', err);
      setError('Failed to load comments and reports.');
    } finally {
      setLoading(false);
    }
  }, [navigate, lang]);

  useEffect(() => {
    fetchModerationData();
  }, [fetchModerationData]);

  const selectTab = (nextTab) => {
    setTab(nextTab);
    setSearchParams({ tab: nextTab }, { replace: true });
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Delete this comment? This cannot be undone.')) return;
    const actionKey = `comment-${commentId}`;
    setActionLoading(actionKey);
    try {
      await fetchWithAuth(`/admin/comments/${commentId}`, { method: 'DELETE' });
      setComments((current) => current.filter((comment) => comment.id !== commentId));
      setReports((current) => current.filter(
        (report) => !(report.type === 'COMMENT' && report.commentId === commentId)
      ));
    } catch (err) {
      console.error('Delete failed:', err);
      window.alert('Failed to delete comment.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDismissReport = async (report) => {
    const actionKey = `${report.type}-${report.id}`;
    setActionLoading(actionKey);
    try {
      const endpoint = report.type === 'PROFILE'
        ? `/admin/profile-reports/${report.id}/dismiss`
        : `/admin/comments/${report.commentId}/dismiss-report`;
      await fetchWithAuth(endpoint, { method: 'POST' });
      setReports((current) => current.filter(
        (item) => !(item.type === report.type && item.id === report.id)
      ));
      if (report.type === 'COMMENT') {
        setComments((current) => current.map((comment) => (
          comment.id === report.commentId ? { ...comment, reported: false } : comment
        )));
      }
    } catch (err) {
      console.error('Dismiss report failed:', err);
      window.alert('Failed to dismiss report.');
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleString('mk-MK', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const profileLink = (item) => item.profileStringId ? (
    <a
      href={`/${lang || 'mk'}/profile/${item.profileStringId}`}
      target="_blank"
      rel="noopener noreferrer"
      className="text-sm text-blue-600 hover:underline whitespace-nowrap"
    >
      {item.profileName || item.profileStringId}
    </a>
  ) : <span className="text-sm text-gray-400">—</span>;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-slate-700" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-red-500 text-lg">{error}</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-200 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <div>
              <h1 className="text-xl font-semibold text-gray-800">Comments &amp; Reports</h1>
              <p className="text-sm text-gray-500">
                {comments.length} comments &nbsp;·&nbsp;
                <span className={reports.length > 0 ? 'text-red-600 font-semibold' : ''}>
                  {reports.length} open reports
                </span>
              </p>
            </div>
          </div>
          <button
            onClick={fetchModerationData}
            className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
          >
            Refresh
          </button>
        </div>

        <div className="flex border-b border-gray-200 px-6">
          <button
            onClick={() => selectTab('comments')}
            className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
              tab === 'comments' ? 'border-slate-900 text-slate-900' : 'border-transparent text-gray-500'
            }`}
          >
            Comments
            <span className="ml-2 text-xs text-gray-400">{comments.length}</span>
          </button>
          <button
            onClick={() => selectTab('reports')}
            className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
              tab === 'reports' ? 'border-red-600 text-red-700' : 'border-transparent text-gray-500'
            }`}
          >
            Reports
            {reports.length > 0 && (
              <span className="ml-2 inline-flex items-center justify-center min-w-5 h-5 px-1 text-xs font-bold text-white bg-red-500 rounded-full">
                {reports.length}
              </span>
            )}
          </button>
        </div>

        {tab === 'comments' && (
          comments.length === 0 ? (
            <div className="py-16 text-center text-gray-400">No comments yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100">
                <thead className="bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <tr>
                    <th className="px-4 py-3 text-left">Author</th>
                    <th className="px-4 py-3 text-left">Profile</th>
                    <th className="px-4 py-3 text-left">Comment</th>
                    <th className="px-4 py-3 text-left">IP</th>
                    <th className="px-4 py-3 text-left">Posted</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {comments.map((comment) => {
                    const actionKey = `comment-${comment.id}`;
                    return (
                      <tr key={comment.id} className={comment.reported ? 'bg-red-50/40' : 'hover:bg-gray-50'}>
                        <td className="px-4 py-3 text-sm font-medium text-gray-800 whitespace-nowrap">{comment.name}</td>
                        <td className="px-4 py-3">{profileLink(comment)}</td>
                        <td className="px-4 py-3 max-w-sm text-sm text-gray-700 whitespace-pre-line break-words">
                          {comment.content}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 font-mono whitespace-nowrap">{comment.ipAddress || '—'}</td>
                        <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{formatDate(comment.createdAt)}</td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          {comment.reported && (
                            <span className="mr-2 inline-flex px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
                              Reported
                            </span>
                          )}
                          <button
                            onClick={() => handleDeleteComment(comment.id)}
                            disabled={actionLoading === actionKey}
                            className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50"
                          >
                            {actionLoading === actionKey ? '...' : 'Delete'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        )}

        {tab === 'reports' && (
          reports.length === 0 ? (
            <div className="py-16 text-center text-gray-400">No open reports.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100">
                <thead className="bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <tr>
                    <th className="px-4 py-3 text-left">Type</th>
                    <th className="px-4 py-3 text-left">Profile</th>
                    <th className="px-4 py-3 text-left">Reported content</th>
                    <th className="px-4 py-3 text-left">Reason</th>
                    <th className="px-4 py-3 text-left">IP</th>
                    <th className="px-4 py-3 text-left">Reported</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {reports.map((report) => {
                    const actionKey = `${report.type}-${report.id}`;
                    return (
                      <tr key={actionKey} className="bg-red-50/20 hover:bg-red-50/40">
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${
                            report.type === 'PROFILE'
                              ? 'bg-purple-100 text-purple-700'
                              : 'bg-amber-100 text-amber-700'
                          }`}>
                            {report.type === 'PROFILE' ? 'Profile' : 'Comment'}
                          </span>
                        </td>
                        <td className="px-4 py-3">{profileLink(report)}</td>
                        <td className="px-4 py-3 max-w-sm">
                          {report.type === 'COMMENT' ? (
                            <>
                              <p className="text-xs font-semibold text-gray-500 mb-1">{report.commentAuthor}</p>
                              <p className="text-sm text-gray-700 whitespace-pre-line break-words">{report.commentContent}</p>
                            </>
                          ) : (
                            <p className="text-sm text-gray-500">Entire memorial profile</p>
                          )}
                        </td>
                        <td className="px-4 py-3 max-w-xs text-sm text-gray-700 italic break-words">
                          {report.reason || 'No reason provided'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 font-mono whitespace-nowrap">{report.ipAddress || '—'}</td>
                        <td className="px-4 py-3 text-sm text-red-600 whitespace-nowrap">{formatDate(report.reportedAt)}</td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <button
                            onClick={() => handleDismissReport(report)}
                            disabled={actionLoading === actionKey}
                            className="px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 disabled:opacity-50"
                          >
                            {actionLoading === actionKey ? '...' : 'Dismiss'}
                          </button>
                          {report.type === 'COMMENT' && (
                            <button
                              onClick={() => handleDeleteComment(report.commentId)}
                              disabled={actionLoading === actionKey}
                              className="ml-2 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50"
                            >
                              Delete comment
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default AdminComments;
